/**
 * Client Profile Page
 * Lets a Project Owner view and edit their personal information.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Avatar,
  Chip,
  Divider,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch
} from '@mui/material';
import PhoneField from '../components/common/PhoneField';
import {
  Person,
  Edit,
  Save,
  Close,
  Email,
  Phone,
  LocationOn,
  Verified,
  Build,
  Settings,
  CameraAlt,
  CheckCircle,
  Visibility,
  Business,
  Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { brand, text, border, bg } from '../theme/colors';

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const ClientProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState('personal');

  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [projects, setProjects] = useState([]);

  // Edit form state — mirrors the profile fields
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    city: '',
    bio: '',
    companyName: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [profileRes, projectsRes] = await Promise.all([
          api.get('/auth/me'),
          api.get('/projects').catch(() => ({ data: { data: { projects: [] } } }))
        ]);

        const userData = profileRes.data.data.user;
        setProfileData(userData);

        const p = userData.profile || {};
        setForm({
          firstName: userData.first_name || p.firstName || '',
          lastName: userData.last_name || p.lastName || '',
          phone: p.phoneNumber || p.phone_number || '',
          city: p.city || '',
          bio: p.bio || '',
          companyName: p.companyName || p.company_name || ''
        });

        setProjects(projectsRes.data.data?.projects || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/auth/users/profile', {
        first_name: form.firstName,
        last_name: form.lastName,
        phone_number: form.phone,
        city: form.city,
        bio: form.bio,
        company_name: form.companyName
      });
      // Refresh profile data
      const res = await api.get('/auth/me');
      setProfileData(res.data.data.user);
      setEditing(false);
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to save changes', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const p = profileData?.profile || {};
    setForm({
      firstName: profileData?.first_name || p.firstName || '',
      lastName: profileData?.last_name || p.lastName || '',
      phone: p.phoneNumber || p.phone_number || '',
      city: p.city || '',
      bio: p.bio || '',
      companyName: p.companyName || p.company_name || ''
    });
    setEditing(false);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>Try Again</Button>
      </Container>
    );
  }

  const profile = profileData?.profile || {};
  const displayName = `${form.firstName || ''} ${form.lastName || ''}`.trim() || profileData?.email || 'Project Owner';
  const initials = `${(form.firstName || '')[0] || ''}${(form.lastName || '')[0] || ''}`.toUpperCase() || 'PO';

  const stats = {
    total: projects.length,
    active: projects.filter(p => ['in_progress', 'planning', 'open_for_bids'].includes(p.status)).length,
    completed: projects.filter(p => p.status === 'completed').length,
    totalBudget: projects.reduce((s, p) => s + parseFloat(p.budget || 0), 0)
  };

  // Profile completion percentage
  const profileFields = [form.firstName, form.lastName, form.phone, form.city];
  const profileCompletion = Math.round((profileFields.filter(Boolean).length / profileFields.length) * 100);

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: <Person sx={{ fontSize: 18 }} /> },
    { id: 'projects', label: 'My Projects', icon: <Build sx={{ fontSize: 18 }} /> },
    { id: 'preferences', label: 'Preferences', icon: <Settings sx={{ fontSize: 18 }} /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>

      {/* ── Profile Header Card ────────────────────────────────── */}
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        {/* Banner */}
        <Box sx={{
          height: 140,
          background: brand.gradient,
          borderRadius: '12px 12px 0 0'
        }} />

        <CardContent sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2.5, mt: '-48px', mb: 2, flexWrap: 'wrap' }}>
            {/* Avatar */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={profile.profileImageUrl}
                sx={{
                  width: 96,
                  height: 96,
                  border: '4px solid white',
                  bgcolor: brand.primary,
                  fontSize: 32,
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                {initials}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: 'absolute',
                  bottom: 2,
                  right: 2,
                  bgcolor: 'white',
                  border: `1px solid ${border.default}`,
                  width: 26,
                  height: 26,
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
              >
                <CameraAlt sx={{ fontSize: 14, color: '#555' }} />
              </IconButton>
              <input ref={fileInputRef} type="file" accept="image/*" hidden />
            </Box>

            {/* Name + badges */}
            <Box sx={{ flex: 1, minWidth: 0, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {displayName}
                </Typography>
                {profileData?.emailVerified && (
                  <Chip
                    icon={<Verified sx={{ fontSize: 14 }} />}
                    label="Verified"
                    size="small"
                    color="success"
                    sx={{ height: 22, fontSize: '0.72rem' }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Project Owner
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {profileData?.email && (
                  <Chip icon={<Email sx={{ fontSize: 14 }} />} label={profileData.email} size="small" variant="outlined" />
                )}
                {form.phone && (
                  <Chip icon={<Phone sx={{ fontSize: 14 }} />} label={form.phone} size="small" variant="outlined" />
                )}
                {form.city && (
                  <Chip icon={<LocationOn sx={{ fontSize: 14 }} />} label={form.city} size="small" variant="outlined" />
                )}
              </Box>
            </Box>

            {/* Edit button */}
            <Box sx={{ pb: 1, flexShrink: 0 }}>
              {!editing ? (
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setEditing(true)}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  Edit Profile
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                    onClick={handleSave}
                    disabled={saving}
                    size="small"
                    sx={{ textTransform: 'none', bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={handleCancel}
                    disabled={saving}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {/* Profile completion bar */}
          {profileCompletion < 100 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Profile completion
                </Typography>
                <Typography variant="caption" fontWeight={600} color={brand.primary}>
                  {profileCompletion}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={profileCompletion}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#e8eaf6',
                  '& .MuiLinearProgress-bar': { bgcolor: brand.primary, borderRadius: 3 }
                }}
              />
              {profileCompletion < 100 && (
                <Typography variant="caption" color="text.secondary">
                  Add your {[!form.firstName && 'name', !form.phone && 'phone number', !form.city && 'city'].filter(Boolean).join(', ')} to complete your profile
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Quick stats */}
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { label: 'Total Projects', value: stats.total },
              { label: 'Active', value: stats.active, color: brand.primary },
              { label: 'Completed', value: stats.completed, color: '#43B581' },
              { label: 'Total Budget', value: formatCurrency(stats.totalBudget) }
            ].map((s) => (
              <Box key={s.label} sx={{ textAlign: 'center', minWidth: 60 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: s.color || text.primary, lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: `2px solid ${border.default}`, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 0 }}>
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              startIcon={tab.icon}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 0,
                textTransform: 'none',
                fontWeight: activeTab === tab.id ? 700 : 400,
                color: activeTab === tab.id ? brand.primary : 'text.secondary',
                borderBottom: activeTab === tab.id ? `2px solid ${brand.primary}` : '2px solid transparent',
                mb: '-2px',
                fontSize: '0.9rem',
                gap: 0.75
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ── Personal Info Tab ─────────────────────────────────────── */}
      {activeTab === 'personal' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  Personal Information
                </Typography>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="First Name"
                      fullWidth
                      value={form.firstName}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                      disabled={!editing}
                      variant={editing ? 'outlined' : 'filled'}
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Last Name"
                      fullWidth
                      value={form.lastName}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                      disabled={!editing}
                      variant={editing ? 'outlined' : 'filled'}
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Email Address"
                      fullWidth
                      value={profileData?.email || ''}
                      disabled
                      variant="filled"
                      helperText="Email cannot be changed here. Contact support if needed."
                      InputProps={{
                        endAdornment: profileData?.emailVerified
                          ? <CheckCircle sx={{ color: '#43B581', fontSize: 18 }} />
                          : <Info sx={{ color: '#f39c12', fontSize: 18 }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PhoneField
                      label="Phone Number"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      disabled={!editing}
                      variant={editing ? 'outlined' : 'filled'}
                      helperText={editing ? 'Pick your country, then the number without the leading 0' : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="City / Location"
                      fullWidth
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      disabled={!editing}
                      variant={editing ? 'outlined' : 'filled'}
                      placeholder="e.g. London, UK"
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Company / Organisation (optional)"
                      fullWidth
                      value={form.companyName}
                      onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                      disabled={!editing}
                      variant={editing ? 'outlined' : 'filled'}
                      placeholder="e.g. Smith Family Holdings"
                      InputProps={{ readOnly: !editing }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Bio (optional)"
                      fullWidth
                      multiline
                      rows={3}
                      value={form.bio}
                      onChange={(e) => setForm({ ...form, bio: e.target.value })}
                      disabled={!editing}
                      variant={editing ? 'outlined' : 'filled'}
                      placeholder="Tell contractors a little about yourself and what you're building…"
                      inputProps={{ maxLength: 300 }}
                      InputProps={{ readOnly: !editing }}
                      helperText={editing ? `${form.bio.length}/300` : ''}
                    />
                  </Grid>
                </Grid>

                {!editing && (
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => setEditing(true)}
                      sx={{ textTransform: 'none', bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                    >
                      Edit Information
                    </Button>
                  </Box>
                )}

                {editing && (
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ textTransform: 'none', bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                    >
                      {saving ? 'Saving…' : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Close />}
                      onClick={handleCancel}
                      disabled={saving}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Account Status
                </Typography>
                <List disablePadding>
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemText primary="Email" secondary={profileData?.email} />
                    <Chip
                      label={profileData?.emailVerified ? 'Verified' : 'Unverified'}
                      size="small"
                      color={profileData?.emailVerified ? 'success' : 'warning'}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemText primary="Account type" secondary="Project Owner" />
                    <Chip label="Active" size="small" color="success" />
                  </ListItem>
                  <Divider />
                  <ListItem disablePadding sx={{ py: 1 }}>
                    <ListItemText
                      primary="Member since"
                      secondary={profileData?.created_at
                        ? new Date(profileData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                        : 'N/A'}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Security
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/account-settings')}
                  sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                >
                  Change Password
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/account-settings')}
                  sx={{ mt: 1, textTransform: 'none', justifyContent: 'flex-start' }}
                >
                  Notification Settings
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── My Projects Tab ───────────────────────────────────────── */}
      {activeTab === 'projects' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>My Projects</Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/projects')}
                size="small"
                sx={{ textTransform: 'none', bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
              >
                View All Projects
              </Button>
            </Box>

            {projects.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                You haven't created any projects yet.{' '}
                <Button size="small" onClick={() => navigate('/projects')} sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}>
                  Create your first project →
                </Button>
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, color: '#555', bgcolor: bg.subtle } }}>
                      <TableCell>Project</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.slice(0, 10).map((project) => (
                      <TableRow
                        key={project.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{project.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {project.project_type || 'General'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{project.location || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatCurrency(project.budget)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={project.status?.replace(/_/g, ' ')}
                            size="small"
                            color={
                              project.status === 'completed' ? 'success' :
                              project.status === 'in_progress' ? 'primary' :
                              project.status === 'open_for_bids' ? 'info' : 'default'
                            }
                            sx={{ textTransform: 'capitalize', fontSize: '0.72rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={project.completion_percentage || 0}
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap', minWidth: 28 }}>
                              {project.completion_percentage || 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}
                          >
                            <Visibility sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {projects.length > 10 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/projects')}
                  sx={{ textTransform: 'none' }}
                >
                  View all {projects.length} projects
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Preferences Tab ───────────────────────────────────────── */}
      {activeTab === 'preferences' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2.5 }}>
                  Display Preferences
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Preferred Currency</InputLabel>
                      <Select defaultValue="USD" label="Preferred Currency">
                        <MenuItem value="USD">US Dollar (USD)</MenuItem>
                        <MenuItem value="GBP">British Pound (GBP)</MenuItem>
                        <MenuItem value="EUR">Euro (EUR)</MenuItem>
                        <MenuItem value="ZAR">South African Rand (ZAR)</MenuItem>
                        <MenuItem value="ZWL">Zimbabwe Dollar (ZWL)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select defaultValue="English" label="Language">
                        <MenuItem value="English">English</MenuItem>
                        <MenuItem value="Shona">Shona</MenuItem>
                        <MenuItem value="Ndebele">Ndebele</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      sx={{ textTransform: 'none', bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                    >
                      Save Preferences
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2.5 }}>
                  Notification Settings
                </Typography>
                <List disablePadding>
                  {[
                    { label: 'Email Notifications', sub: 'Project updates, milestone alerts, bid activity', on: true },
                    { label: 'SMS / WhatsApp', sub: 'Urgent updates and payment notifications', on: false },
                    { label: 'Milestone Reminders', sub: 'Alerts before auto-release deadlines', on: true },
                    { label: 'New Quotations Received', sub: 'When a contractor submits a quotation on your project', on: true },
                    { label: 'Payment Confirmations', sub: 'When funds are released or received', on: true }
                  ].map((item, i) => (
                    <React.Fragment key={item.label}>
                      {i > 0 && <Divider />}
                      <ListItem disablePadding sx={{ py: 1 }}>
                        <ListItemText
                          primary={item.label}
                          secondary={item.sub}
                          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: '0.78rem' }}
                        />
                        <ListItemSecondaryAction>
                          <Switch edge="end" defaultChecked={item.on} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Snackbar ─────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClientProfile;
